/**
 * User Preferences Migration Utility
 * 
 * Automatically upgrades user preferences to the latest schema version.
 * This system allows adding new preference fields over time without breaking
 * existing users or requiring backend validation.
 * 
 * Design Principles:
 * - Always preserve unknown fields (forward compatibility)
 * - Migrations are idempotent (safe to run multiple times)
 * - Version tracking via `version` field in JSON
 * - Sequential migration chain for multi-version jumps (V1→V2→V3)
 */

// Current schema version - increment when adding breaking changes
export const CURRENT_SCHEMA_VERSION = 2;

/**
 * Migration chain: maps from version → upgrade function
 * Each function takes preferences at version N and returns version N+1
 */
const MIGRATION_CHAIN = {
  // V1 → V2: Add gamification tracking
  1: (prefs) => {
    return {
      ...prefs, // Preserve all existing fields
      gamification: {
        lastLoginDate: null,        // ISO date string of last login
        currentStreak: 0,            // Current consecutive days streak
        longestStreak: 0,            // All-time best streak
        totalLogins: 0,              // Lifetime login count
        streakStartDate: null,       // When current streak began
        achievements: []             // Array of unlocked achievement IDs
      },
      version: 2,
      migratedAt: new Date().toISOString() // Track when migration occurred
    };
  }
  
  // Future migrations:
  // 2: (prefs) => ({ ...prefs, newFeature: defaultValue, version: 3 }),
  // 3: (prefs) => ({ ...prefs, anotherFeature: {}, version: 4 }),
};

/**
 * Upgrade preferences from any version to CURRENT_SCHEMA_VERSION
 * 
 * @param {Object|null} prefs - Raw preferences from server (may be null/undefined)
 * @returns {Object} { upgraded: boolean, preferences: {...}, fromVersion: number, toVersion: number }
 */
export function upgradePreferences(prefs) {
  // Handle missing or invalid preferences
  if (!prefs || typeof prefs !== 'object') {
    // Create fresh V2 preferences with defaults
    return {
      upgraded: true,
      preferences: {
        version: CURRENT_SCHEMA_VERSION,
        theme: null,
        language: null,
        density: null,
        experiments: {},
        gamification: {
          lastLoginDate: null,
          currentStreak: 0,
          longestStreak: 0,
          totalLogins: 0,
          streakStartDate: null,
          achievements: []
        },
        updatedAt: new Date().toISOString()
      },
      fromVersion: 0,
      toVersion: CURRENT_SCHEMA_VERSION
    };
  }

  // Detect current version (default to 1 if not specified)
  const currentVersion = prefs.version || 1;
  
  // Already on latest version - no upgrade needed
  if (currentVersion >= CURRENT_SCHEMA_VERSION) {
    return {
      upgraded: false,
      preferences: prefs,
      fromVersion: currentVersion,
      toVersion: currentVersion
    };
  }

  // Apply sequential migrations (e.g., V1→V2→V3 if user was on V1 and current is V3)
  let upgraded = { ...prefs };
  let version = currentVersion;

  while (version < CURRENT_SCHEMA_VERSION) {
    const migrationFn = MIGRATION_CHAIN[version];
    
    if (!migrationFn) {
      console.error(`[PreferencesMigration] Missing migration function for version ${version}→${version + 1}`);
      break;
    }

    try {
      upgraded = migrationFn(upgraded);
      version = upgraded.version;
      // eslint-disable-next-line no-console
      console.log(`[PreferencesMigration] Applied migration ${version - 1}→${version}`);
    } catch (error) {
      console.error(`[PreferencesMigration] Migration ${version}→${version + 1} failed:`, error);
      break;
    }
  }

  return {
    upgraded: true,
    preferences: upgraded,
    fromVersion: currentVersion,
    toVersion: upgraded.version
  };
}

/**
 * Validate that preferences object has required V2 fields
 * Useful for debugging and ensuring migrations worked correctly
 * 
 * @param {Object} prefs - Preferences to validate
 * @returns {boolean} True if valid V2 structure
 */
export function isValidV2Schema(prefs) {
  if (!prefs || typeof prefs !== 'object') return false;
  if (prefs.version !== 2) return false;
  
  // Check required V2 fields
  if (!prefs.gamification || typeof prefs.gamification !== 'object') return false;
  
  const requiredGamificationFields = [
    'lastLoginDate',
    'currentStreak', 
    'longestStreak',
    'totalLogins',
    'streakStartDate',
    'achievements'
  ];
  
  return requiredGamificationFields.every(field => field in prefs.gamification);
}

/**
 * Get default V2 preferences structure
 * Useful for testing and seeding new users
 * 
 * @param {Object} overrides - Optional field overrides
 * @returns {Object} Complete V2 preferences
 */
export function getDefaultV2Preferences(overrides = {}) {
  return {
    version: 2,
    theme: 'docuHounds',
    language: null,
    density: null,
    experiments: {},
    gamification: {
      lastLoginDate: null,
      currentStreak: 0,
      longestStreak: 0,
      totalLogins: 0,
      streakStartDate: null,
      achievements: []
    },
    updatedAt: new Date().toISOString(),
    ...overrides
  };
}
